<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import LineItem from '@/components/checkout/summary/LineItem.vue';
import LineItemsGrouped from '@/components/checkout/summary/LineItemsGrouped.vue';
import { useCheckout } from '@/composables/useCheckout';
import { filterByAutoDeliveryStatus, NutsLineItem } from '@/lib/cart/lineItem';
import { Shipment } from '@/utils/cart';

const props = withDefaults(
  defineProps<{
    containsAutoDelivery?: boolean;
    lineItems: NutsLineItem[];
    shipments?: Shipment[];
    specialDeliveryLineItems: NutsLineItem[];
    unassignedLineItems: NutsLineItem[];
  }>(),
  {
    containsAutoDelivery: false,
    specialDeliveryLineItems: () => [],
    unassignedLineItems: () => [],
  },
);

const store = useStore();
const { cxMode } = useCheckout(store);
const collapsed = ref(false);
const hasMultipleShipments = computed(() => (props.shipments?.length || 0) > 1);

onMounted(() => {
  if (window.innerWidth > 768 && !cxMode.value) collapsed.value = true;
});
</script>

<template>
  <div
    :class="{
      'divide-solid divide-y-2 divide-neutral-500':
        shipments?.length || specialDeliveryLineItems.length,
    }"
  >
    <template
      v-if="shipments?.length || unassignedLineItems.length || specialDeliveryLineItems.length"
    >
      <div v-if="specialDeliveryLineItems.length">
        <h3 class="mt-8 text-base font-semibold text-black md:text-lg font-sophia-pro">
          Special Delivery Items
        </h3>
        <div class="divide-y divide-solid divide-neutral-200">
          <LineItem
            v-for="lineItem in specialDeliveryLineItems"
            :key="lineItem.id"
            :lineItem="lineItem"
          />
        </div>
      </div>
      <div v-if="unassignedLineItems.length">
        <LineItemsGrouped
          :autoDeliveryItems="filterByAutoDeliveryStatus(unassignedLineItems, true)"
          :oneTimeItems="filterByAutoDeliveryStatus(unassignedLineItems)"
        />
      </div>
      <div
        v-for="(shipment, i) in shipments"
        :key="shipment.key"
        :class="{
          'pt-4':
            i > 0 ||
            (i === 0 && unassignedLineItems.length) ||
            (i === 0 && specialDeliveryLineItems.length),
        }"
      >
        <div
          v-if="hasMultipleShipments || unassignedLineItems.length"
          class="text-sm font-semibold md:text-base"
        >
          Address {{ shipment.multishipSerial }}:
          {{ shipment.address.name }}
          <template v-if="shipment.address.city"> in {{ shipment.address.city }} </template>
        </div>
        <LineItemsGrouped
          :autoDeliveryItems="filterByAutoDeliveryStatus(shipment.lineItems, true)"
          :oneTimeItems="filterByAutoDeliveryStatus(shipment.lineItems)"
        />
      </div>
    </template>
    <div v-else class="divide-y divide-solid divide-neutral-200">
      <LineItem v-for="lineItem in lineItems" :key="lineItem.id" :lineItem="lineItem" />
    </div>
  </div>
</template>
